import { useId } from 'react'

import { chakra } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'

const spin = keyframes({
  '0%': { transform: 'rotate(0deg)' },
  '100%': { transform: 'rotate(360deg)' },
})

/**
 * Build an SVG pie-slice path that sweeps clockwise.
 * @param c      center x/y
 * @param r      radius
 * @param start  start angle in degrees, clockwise from 12 o'clock
 * @param value  0-1 progress
 */
function pieSlicePath(c: number, r: number, start: number, value: number) {
  const startRad = (start * Math.PI) / 180
  const endRad = ((start + value * 360) * Math.PI) / 180

  const x1 = c + r * Math.sin(startRad)
  const y1 = c - r * Math.cos(startRad)
  const x2 = c + r * Math.sin(endRad)
  const y2 = c - r * Math.cos(endRad)

  const largeArc = value > 0.5 ? 1 : 0

  return `M ${c},${c} L ${x1},${y1} A ${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`
}

export interface ProgressIconProps {
  /** Progress value from 0 to 1 */
  value?: number
  /** Whether the outer ring should spin */
  loading?: boolean
  /** Chakra UI color palette name (e.g. "yellow", "accent", "emerald") */
  colorPalette?: string
  /** Icon size */
  size?: number | string
  className?: string
}

export const ProgressIcon = ({
  value = 0,
  loading = false,
  colorPalette = 'gray',
  size = '1em',
  className,
}: ProgressIconProps) => {
  const clampedValue = Math.max(0, Math.min(1, value))

  // Circle math
  const center = 12
  const outerRadius = 10.5
  const innerRadius = 7
  const outerCircumference = 2 * Math.PI * outerRadius

  const color = `var(--chakra-colors-${colorPalette}-solid)`

  const id = useId()

  return (
    <chakra.svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      color="colorPalette.solid"
      colorPalette={colorPalette}
      className={className}
      css={{
        '& [data-spinner]': {
          animation: `${spin} 1s linear infinite`,
          transformOrigin: 'center',
        },
      }}
      flexShrink={0}
    >
      <defs>
        {loading && (
          <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="1" />
            <stop offset="60%" stopColor={color} stopOpacity="0.4" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        )}
      </defs>

      {loading ? (
        <circle
          data-spinner
          cx={center}
          cy={center}
          r={outerRadius}
          stroke={`url(#${id})`}
          strokeWidth={3}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${outerCircumference * 0.7} ${outerCircumference * 0.3}`}
          transform={`rotate(-90 ${center} ${center})`}
        />
      ) : (
        <circle
          cx={center}
          cy={center}
          r={outerRadius}
          stroke="currentColor"
          strokeWidth={3}
          fill="none"
        />
      )}

      {clampedValue > 0 &&
        (clampedValue >= 1 ? (
          <circle cx={center} cy={center} r={innerRadius} fill="currentColor" />
        ) : (
          <path
            d={pieSlicePath(center, innerRadius, 0, clampedValue)}
            fill="currentColor"
          />
        ))}
    </chakra.svg>
  )
}
